/**
 * Componenti Schema.org JSON-LD per SEO
 * Inserisce structured data nel documento per migliorare l'indicizzazione
 */
import { useEffect } from 'react';

const SITE_URL = 'https://restainpace.it';
const SITE_NAME = 'Resta in Pace';
const LOGO_URL = `${SITE_URL}/logo.png`;

/**
 * Componente base per inserire JSON-LD
 */
const JsonLd = ({ id, data }) => {
  useEffect(() => {
    if (!data) return;

    // Rimuovi script esistente con stesso id
    const existingScript = document.getElementById(id);
    if (existingScript) {
      existingScript.remove();
    }

    // Crea nuovo script
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = id;
    script.text = JSON.stringify(data);
    document.head.appendChild(script);

    // Cleanup
    return () => {
      const scriptToRemove = document.getElementById(id);
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
   
  }, [id, data]);

  return null;
};

export { JsonLd };

/**
 * Schema.org per Organizzazione (homepage)
 */
export const OrganizationSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": SITE_NAME,
    "url": SITE_URL,
    "logo": LOGO_URL,
    "description": "Portale italiano dedicato ai necrologi, commemorazioni funebri e servizi per il lutto.",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "IT"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "url": `${SITE_URL}/contatti`,
      "availableLanguage": ["Italian"]
    }
  };

  return <JsonLd id="schema-organization" data={schema} />;
};

/**
 * Schema.org per Necrologio
 */
export const ObituarySchema = ({ necrologio }) => {
  if (!necrologio) return null;

  const nomeCompleto = `${necrologio.nome || ''} ${necrologio.cognome || ''}`.trim();
  const necrologioId = necrologio._id || necrologio.id;

  const schemas = [];

  // Person schema
  schemas.push({
    "@context": "https://schema.org",
    "@type": "Person",
    "name": nomeCompleto,
    "birthDate": necrologio.data_nascita,
    "deathDate": necrologio.data_morte,
    "birthPlace": necrologio.luogo_nascita,
    "deathPlace": necrologio.luogo_morte,
    "image": necrologio.foto_url,
    "description": necrologio.testo_commemorativo?.substring(0, 200)
  });

  // Article schema
  schemas.push({
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": `Necrologio di ${nomeCompleto}`,
    "description": necrologio.testo_commemorativo?.substring(0, 160),
    "image": necrologio.foto_url,
    "datePublished": necrologio.created_at,
    "dateModified": necrologio.updated_at || necrologio.created_at,
    "author": {
      "@type": "Organization",
      "name": necrologio.agenzia_nome || SITE_NAME
    },
    "publisher": {
      "@type": "Organization",
      "name": SITE_NAME,
      "logo": { "@type": "ImageObject", "url": LOGO_URL }
    },
    "mainEntityOfPage": `${SITE_URL}/necrologio/${necrologioId}`
  });

  // Funeral Event schema (if date exists)
  if (necrologio.data_funerale) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "Event",
      "name": `Funerale di ${nomeCompleto}`,
      "startDate": necrologio.data_funerale,
      "location": {
        "@type": "Place",
        "name": necrologio.chiesa_nome || necrologio.luogo_funerale || "Chiesa",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": necrologio.citta_funerale || necrologio.comune,
          "addressCountry": "IT"
        }
      }
    });
  }

  // Combine all schemas
  const combinedSchema = schemas.length === 1 ? schemas[0] : {
    "@context": "https://schema.org",
    "@graph": schemas
  };

  return <JsonLd id="schema-obituary" data={combinedSchema} />;
};

/**
 * Schema.org per Agenzia Funebre
 */
export const FuneralHomeSchema = ({ agenzia }) => {
  if (!agenzia) return null;

  const schema = {
    "@context": "https://schema.org",
    "@type": "FuneralHome",
    "name": agenzia.nome_azienda || agenzia.ragione_sociale,
    "description": agenzia.descrizione,
    "image": agenzia.logo_url,
    "telephone": agenzia.telefono,
    "email": agenzia.email,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": agenzia.indirizzo,
      "addressLocality": agenzia.città,
      "addressRegion": agenzia.provincia,
      "postalCode": agenzia.cap,
      "addressCountry": "IT"
    },
    "url": `${SITE_URL}${(agenzia.provincia_slug && agenzia.citta_slug && agenzia.slug) ? `/agenzia/${agenzia.provincia_slug}/${agenzia.citta_slug}/${agenzia.slug}` : `/agenzia/${agenzia.id}`}`,
    "priceRange": "€€"
  };

  if (agenzia.rating) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      "ratingValue": agenzia.rating,
      "reviewCount": agenzia.num_recensioni || 1
    };
  }

  return <JsonLd id="schema-funeralhome" data={schema} />;
};

/**
 * Schema.org per Articolo Blog
 */
export const BlogArticleSchema = ({ article }) => {
  if (!article) return null;

  const schema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": article.titolo,
    "description": article.estratto || article.contenuto?.substring(0, 160),
    "image": article.immagine_url,
    "datePublished": article.created_at,
    "dateModified": article.updated_at || article.created_at,
    "author": {
      "@type": "Person",
      "name": article.autore_nome || article.autore
    },
    "publisher": {
      "@type": "Organization",
      "name": SITE_NAME,
      "logo": { "@type": "ImageObject", "url": LOGO_URL }
    },
    "mainEntityOfPage": `${SITE_URL}/blog/${article.slug || article.id}`,
    "keywords": article.tags?.join(", ")
  };

  return <JsonLd id="schema-blogpost" data={schema} />;
};

/**
 * Schema.org per WebSite (ricerca)
 */
export const WebsiteSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": SITE_NAME,
    "url": SITE_URL,
    "potentialAction": {
      "@type": "SearchAction",
      "target": `${SITE_URL}/necrologi?search={search_term_string}`,
      "query-input": "required name=search_term_string"
    }
  };

  return <JsonLd id="schema-website" data={schema} />;
};

/**
 * Schema.org per Breadcrumb
 */
export const BreadcrumbSchema = ({ items }) => {
  if (!items || items.length === 0) return null;

  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url ? `${SITE_URL}${item.url}` : undefined
    }))
  };

  return <JsonLd id="schema-breadcrumb" data={schema} />;
};

export default {
  OrganizationSchema,
  ObituarySchema,
  FuneralHomeSchema,
  BlogArticleSchema,
  WebsiteSchema,
  BreadcrumbSchema
};
